import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { Loader2, Save, Plus, Trash2, Sparkles, ListChecks, Inbox, Send, GripVertical } from "lucide-react";

interface EnterpriseSettingsRow {
  id?: string;
  user_id?: string;
  default_hourly_rate: number | null;
  default_payment_terms: string | null;
  custom_contract_intro: string | null;
  custom_contract_outro: string | null;
  ai_brand_voice: string | null;
  sla_tier: string;
  custom_team_seats: number;
  account_manager_id: string | null;
}

interface CustomField {
  id?: string;
  field_key: string;
  field_label: string;
  field_type: string;
  applies_to: string;
  is_required: boolean;
  help_text: string | null;
  options: string[];
  display_order: number;
}

interface CustomizationRequest {
  id: string;
  title: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  admin_response: string | null;
  created_at: string;
  completed_at: string | null;
}

const FIELD_TYPES = ["text", "textarea", "number", "select", "checkbox", "date"];
const APPLIES_TO = [
  { value: "proposal", label: "Proposals" },
  { value: "contract", label: "Contracts" },
  { value: "both", label: "Both" },
];
const REQ_CATEGORIES = [
  { value: "workflow", label: "Workflow change" },
  { value: "template", label: "Template/branding" },
  { value: "integration", label: "Integration" },
  { value: "ai_behavior", label: "AI behavior" },
  { value: "other", label: "Other" },
];

export const EnterpriseSelfServe = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<EnterpriseSettingsRow | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const [fields, setFields] = useState<CustomField[]>([]);
  const [editingField, setEditingField] = useState<CustomField | null>(null);

  const [requests, setRequests] = useState<CustomizationRequest[]>([]);
  const [newReq, setNewReq] = useState({ title: "", description: "", category: "workflow", priority: "normal" });
  const [submittingReq, setSubmittingReq] = useState(false);

  const fetchAll = async () => {
    if (!user) return;
    setLoading(true);
    const [settingsRes, fieldsRes, reqRes] = await Promise.all([
      (supabase as any).from("enterprise_settings").select("*").eq("user_id", user.id).maybeSingle(),
      (supabase as any).from("enterprise_custom_fields").select("*").eq("user_id", user.id).order("display_order", { ascending: true }),
      (supabase as any).from("enterprise_customization_requests").select("*").eq("user_id", user.id).order("created_at", { ascending: false }),
    ]);

    if (settingsRes.data) {
      setSettings(settingsRes.data);
    } else {
      // Lazily create default row if missing
      const { data: created } = await (supabase as any).from("enterprise_settings").insert({ user_id: user.id }).select().single();
      setSettings(created || {
        default_hourly_rate: null, default_payment_terms: null,
        custom_contract_intro: null, custom_contract_outro: null,
        ai_brand_voice: null, sla_tier: "standard", custom_team_seats: 25, account_manager_id: null,
      });
    }
    setFields(((fieldsRes.data || []) as any[]).map((f) => ({
      ...f, options: Array.isArray(f.options) ? f.options : [],
    })));
    setRequests((reqRes.data || []) as CustomizationRequest[]);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, [user]);

  const saveSettings = async () => {
    if (!settings || !user) return;
    setSavingSettings(true);
    const { error } = await (supabase as any)
      .from("enterprise_settings")
      .update({
        default_hourly_rate: settings.default_hourly_rate,
        default_payment_terms: settings.default_payment_terms,
        custom_contract_intro: settings.custom_contract_intro,
        custom_contract_outro: settings.custom_contract_outro,
        ai_brand_voice: settings.ai_brand_voice,
      })
      .eq("user_id", user.id);
    if (error) toast({ title: "Save failed", description: error.message, variant: "destructive" });
    else toast({ title: "Defaults saved" });
    setSavingSettings(false);
  };

  const openNewField = () => {
    setEditingField({
      field_key: "", field_label: "", field_type: "text",
      applies_to: "proposal", is_required: false, help_text: "",
      options: [], display_order: fields.length,
    });
  };

  const saveField = async () => {
    if (!editingField || !user) return;
    if (!editingField.field_label.trim()) {
      toast({ title: "Label required", variant: "destructive" });
      return;
    }
    const key = editingField.field_key.trim() || editingField.field_label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    const payload: any = {
      user_id: user.id,
      field_key: key,
      field_label: editingField.field_label,
      field_type: editingField.field_type,
      applies_to: editingField.applies_to,
      is_required: editingField.is_required,
      help_text: editingField.help_text || null,
      options: editingField.field_type === "select" ? editingField.options : [],
      display_order: editingField.display_order,
    };
    const res = editingField.id
      ? await (supabase as any).from("enterprise_custom_fields").update(payload).eq("id", editingField.id)
      : await (supabase as any).from("enterprise_custom_fields").insert(payload);
    if (res.error) {
      toast({ title: "Save failed", description: res.error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Field saved" });
    setEditingField(null);
    fetchAll();
  };

  const deleteField = async (id: string) => {
    const { error } = await (supabase as any).from("enterprise_custom_fields").delete().eq("id", id);
    if (error) toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    else { toast({ title: "Field removed" }); fetchAll(); }
  };

  const submitRequest = async () => {
    if (!user) return;
    if (!newReq.title.trim() || !newReq.description.trim()) {
      toast({ title: "Title and description required", variant: "destructive" });
      return;
    }
    setSubmittingReq(true);
    const { error } = await (supabase as any).from("enterprise_customization_requests").insert({
      user_id: user.id,
      title: newReq.title,
      description: newReq.description,
      category: newReq.category,
      priority: newReq.priority,
    });
    if (error) toast({ title: "Submission failed", description: error.message, variant: "destructive" });
    else {
      toast({ title: "Request submitted", description: "Your account manager will follow up." });
      setNewReq({ title: "", description: "", category: "workflow", priority: "normal" });
      fetchAll();
    }
    setSubmittingReq(false);
  };

  const statusTone = (s: string) => {
    switch (s) {
      case "completed": return "bg-success/15 text-success border-success/30";
      case "in_progress": return "bg-info/15 text-info border-info/30";
      case "reviewing": return "bg-warning/15 text-warning border-warning/30";
      case "declined": return "bg-destructive/15 text-destructive border-destructive/30";
      default: return "bg-muted text-muted-foreground border-border";
    }
  };

  if (loading || !settings) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Tier overview */}
      <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-foreground flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Enterprise Customization</CardTitle>
              <CardDescription>Configure your defaults, request new behaviors, and define custom fields.</CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="border-primary/40 text-primary">SLA: {settings.sla_tier}</Badge>
              <Badge variant="outline" className="border-border">Seats: {settings.custom_team_seats}</Badge>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Defaults */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-foreground">Default Templates & Branding</CardTitle>
          <CardDescription>Auto-applied to new proposals and contracts.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Default Hourly Rate ($)</Label>
              <Input
                type="number" min={0} step="0.01"
                value={settings.default_hourly_rate ?? ""}
                onChange={(e) => setSettings({ ...settings, default_hourly_rate: e.target.value === "" ? null : Number(e.target.value) })}
                className="mt-1 bg-secondary border-border"
                placeholder="e.g. 45.00"
              />
            </div>
            <div>
              <Label className="text-muted-foreground">Default Payment Terms</Label>
              <Input
                value={settings.default_payment_terms ?? ""}
                onChange={(e) => setSettings({ ...settings, default_payment_terms: e.target.value })}
                className="mt-1 bg-secondary border-border"
                placeholder="e.g. Net 15"
              />
            </div>
          </div>
          <div>
            <Label className="text-muted-foreground">Custom Contract Intro</Label>
            <Textarea
              rows={3}
              value={settings.custom_contract_intro ?? ""}
              onChange={(e) => setSettings({ ...settings, custom_contract_intro: e.target.value })}
              className="mt-1 bg-secondary border-border"
              placeholder="Prepended to every generated contract..."
            />
          </div>
          <div>
            <Label className="text-muted-foreground">Custom Contract Outro</Label>
            <Textarea
              rows={3}
              value={settings.custom_contract_outro ?? ""}
              onChange={(e) => setSettings({ ...settings, custom_contract_outro: e.target.value })}
              className="mt-1 bg-secondary border-border"
              placeholder="Appended to every generated contract..."
            />
          </div>
          <div>
            <Label className="text-muted-foreground">AI Brand Voice</Label>
            <Textarea
              rows={3}
              value={settings.ai_brand_voice ?? ""}
              onChange={(e) => setSettings({ ...settings, ai_brand_voice: e.target.value })}
              className="mt-1 bg-secondary border-border"
              placeholder="e.g. Authoritative, formal, references our 25 years of experience..."
            />
            <p className="text-xs text-muted-foreground mt-1">Influences AI-generated proposal copy.</p>
          </div>
          <Button onClick={saveSettings} disabled={savingSettings} className="gap-2">
            {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Defaults
          </Button>
        </CardContent>
      </Card>

      {/* Custom fields */}
      <Card className="border-border bg-card">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-foreground"><ListChecks className="h-5 w-5 text-primary" /> Custom Fields</CardTitle>
            <CardDescription>Add bespoke fields to proposals and contracts.</CardDescription>
          </div>
          <Button onClick={openNewField} variant="outline" size="sm" className="gap-1.5">
            <Plus className="h-4 w-4" /> Add Field
          </Button>
        </CardHeader>
        <CardContent>
          {fields.length === 0 && !editingField ? (
            <div className="text-center py-8 text-muted-foreground text-sm">No custom fields yet. Add one to extend your forms.</div>
          ) : (
            <div className="space-y-2">
              {fields.map((f) => (
                <div key={f.id} className="flex items-center gap-3 p-3 rounded-md border border-border bg-secondary/40">
                  <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground truncate">{f.field_label}</span>
                      <Badge variant="outline" className="text-[10px]">{f.field_type}</Badge>
                      <Badge variant="outline" className="text-[10px]">{f.applies_to}</Badge>
                      {f.is_required && <Badge variant="outline" className="text-[10px] border-warning/40 text-warning">Required</Badge>}
                    </div>
                    {f.help_text && <p className="text-xs text-muted-foreground mt-0.5 truncate">{f.help_text}</p>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setEditingField({ ...f })}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => f.id && deleteField(f.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {editingField && (
            <>
              <Separator className="my-4" />
              <div className="space-y-3 p-4 rounded-md border border-primary/30 bg-primary/5">
                <h4 className="font-medium text-foreground">{editingField.id ? "Edit Field" : "New Field"}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Label *</Label>
                    <Input value={editingField.field_label} onChange={(e) => setEditingField({ ...editingField, field_label: e.target.value })} className="mt-1 bg-background border-border" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Key (auto if blank)</Label>
                    <Input value={editingField.field_key} onChange={(e) => setEditingField({ ...editingField, field_key: e.target.value })} className="mt-1 bg-background border-border" placeholder="my_field" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Type</Label>
                    <Select value={editingField.field_type} onValueChange={(v) => setEditingField({ ...editingField, field_type: v })}>
                      <SelectTrigger className="mt-1 bg-background border-border"><SelectValue /></SelectTrigger>
                      <SelectContent>{FIELD_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Applies To</Label>
                    <Select value={editingField.applies_to} onValueChange={(v) => setEditingField({ ...editingField, applies_to: v })}>
                      <SelectTrigger className="mt-1 bg-background border-border"><SelectValue /></SelectTrigger>
                      <SelectContent>{APPLIES_TO.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Help text</Label>
                  <Input value={editingField.help_text || ""} onChange={(e) => setEditingField({ ...editingField, help_text: e.target.value })} className="mt-1 bg-background border-border" />
                </div>
                {editingField.field_type === "select" && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Options (comma-separated)</Label>
                    <Input
                      value={editingField.options.join(", ")}
                      onChange={(e) => setEditingField({ ...editingField, options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                      className="mt-1 bg-background border-border"
                      placeholder="Option A, Option B, Option C"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Switch checked={editingField.is_required} onCheckedChange={(c) => setEditingField({ ...editingField, is_required: c })} />
                  <Label className="text-sm text-foreground">Required</Label>
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveField} size="sm" className="gap-1.5"><Save className="h-4 w-4" /> Save Field</Button>
                  <Button onClick={() => setEditingField(null)} variant="ghost" size="sm">Cancel</Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Customization requests */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground"><Inbox className="h-5 w-5 text-primary" /> Customization Requests</CardTitle>
          <CardDescription>Need a workflow change, integration, or template tweak? Let your account manager know.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3 p-4 rounded-md border border-border bg-secondary/30">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Title *</Label>
                <Input value={newReq.title} onChange={(e) => setNewReq({ ...newReq, title: e.target.value })} className="mt-1 bg-background border-border" placeholder="Auto-tag proposals over $50k" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Category</Label>
                <Select value={newReq.category} onValueChange={(v) => setNewReq({ ...newReq, category: v })}>
                  <SelectTrigger className="mt-1 bg-background border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>{REQ_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Description *</Label>
              <Textarea rows={4} value={newReq.description} onChange={(e) => setNewReq({ ...newReq, description: e.target.value })} className="mt-1 bg-background border-border" placeholder="Describe the change you need..." />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">Priority</Label>
                <Select value={newReq.priority} onValueChange={(v) => setNewReq({ ...newReq, priority: v })}>
                  <SelectTrigger className="w-32 bg-background border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={submitRequest} disabled={submittingReq} className="gap-2">
                {submittingReq ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Submit Request
              </Button>
            </div>
          </div>

          {requests.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground">Your Requests</h4>
              {requests.map((r) => (
                <div key={r.id} className="p-3 rounded-md border border-border bg-secondary/40">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground">{r.title}</span>
                        <Badge variant="outline" className="text-[10px]">{r.category}</Badge>
                        <Badge variant="outline" className={`text-[10px] ${statusTone(r.status)}`}>{r.status.replace("_", " ")}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{r.description}</p>
                      {r.admin_response && (
                        <div className="mt-2 p-2 rounded bg-primary/5 border border-primary/20">
                          <p className="text-[10px] uppercase tracking-wide text-primary mb-1">Account Manager Response</p>
                          <p className="text-xs text-foreground whitespace-pre-wrap">{r.admin_response}</p>
                        </div>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EnterpriseSelfServe;
